'use client';

import * as React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { useUser } from '@/hooks/v2/use-user';

export interface CommunityAccessGuardProps {
  children: React.ReactNode;
}

/**
 * A user can have an account login but zero community memberships (e.g. removed
 * from their last community elsewhere). Rather than render a half-broken
 * dashboard, show a clear "no access" screen. Managers/account admins are
 * exempt because they may legitimately operate above the community level.
 */
export function CommunityAccessGuard({ children }: CommunityAccessGuardProps): React.JSX.Element {
  const { communities, account, isLoading } = useUser();

  const accountRoles = (account?.roles ?? []) as string[];
  const isAccountAdmin = ['account_owner', 'account_admin', 'account_manager'].some((r) => accountRoles.includes(r));

  if (isLoading) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  if (!isAccountAdmin && Array.isArray(communities) && communities.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Stack spacing={2} sx={{ maxWidth: 420, textAlign: 'center' }}>
          <Typography variant="h6">No community access</Typography>
          <Typography color="text.secondary" variant="body2">
            You don&apos;t currently have access to any community. Please contact your community administrator if you
            believe this is a mistake.
          </Typography>
          <Button href="https://hoaone.ai/help" target="_blank" rel="noopener noreferrer" variant="outlined">
            Get help
          </Button>
        </Stack>
      </Box>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
}
